import React, { useState, useEffect, useCallback } from 'react';
import { X, Play, Plus, ThumbsUp, Volume2, VolumeX, Check, Trophy, Tv, Clock, Radio } from 'lucide-react';
import { fetchMovieDetails, fetchTVDetails, getImageUrl, imageSize, getTrailerKey } from '../services/tmdb';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import AwardRating, { AggregateRating, ContentRating } from './AwardRating';
import axios from 'axios';
import { API_URL } from '../config/api';

const ContentModal = ({ content, isOpen, onClose, user }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrailer, setShowTrailer] = useState(false);
  const [muted, setMuted] = useState(true);
  const [showRating, setShowRating] = useState(false);
  
  // Rating states
  const [userRating, setUserRating] = useState(0);
  const [seasonRatings, setSeasonRatings] = useState({});
  const [episodeRatings, setEpisodeRatings] = useState({});
  const [aggregateRating, setAggregateRating] = useState({ average: 0, count: 0 });
  const [ratingLoading, setRatingLoading] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [nextEpisode, setNextEpisode] = useState(null);
  const [nextEpLoading, setNextEpLoading] = useState(false);
  const [countdown, setCountdown] = useState('');

  const mediaType = content?.media_type || (content?.title ? 'movie' : 'tv');

  useEffect(() => {
    if (content && isOpen) {
      loadDetails();
      if (user) {
        loadUserRatings();
        checkWatchlistStatus();
      } else {
        setIsInWatchlist(false);
      }
      // Fetch next episode for TV shows
      if (mediaType === 'tv') {
        fetchNextEpisode();
      } else {
        setNextEpisode(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, isOpen, user]);

  const checkWatchlistStatus = async () => {
    if (!user || !content) return;
    try {
      const res = await axios.get(`${API_URL}/api/watchlist/${user.id}/check/${content.id}`);
      setIsInWatchlist(res.data.in_watchlist === true);
    } catch {
      setIsInWatchlist(false);
    }
  };

  const fetchNextEpisode = useCallback(async () => {
    if (!content) return;
    const showName = content.name || content.title;
    if (!showName) return;
    setNextEpLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reality-hub/next-episode`, {
        params: { show_name: showName },
      });
      setNextEpisode(res.data);
    } catch {
      setNextEpisode(null);
    }
    setNextEpLoading(false);
  }, [content, mediaType]);

  // Live countdown timer
  useEffect(() => {
    if (!nextEpisode?.found || !nextEpisode?.air_datetime) {
      setCountdown('');
      return;
    }
    const update = () => {
      const now = new Date();
      const air = new Date(nextEpisode.air_datetime);
      const diff = air - now;
      if (diff <= 0) {
        // Check if currently airing (within ~90 min of start)
        if (diff > -90 * 60 * 1000) {
          setCountdown('LIVE_NOW');
        } else {
          setCountdown('AIRED');
        }
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      let parts = [];
      if (d > 0) parts.push(`${d} day${d !== 1 ? 's' : ''}`);
      if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
      parts.push(`${String(m).padStart(2, '0')} min${m !== 1 ? 's' : ''}`);
      parts.push(`${String(s).padStart(2, '0')} sec${s !== 1 ? 's' : ''}`);
      setCountdown(parts.join(' '));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [nextEpisode]);

  const handleToggleWatchlist = async () => {
    if (!user) {
      toast.error('Please log in to add to your Watchlist');
      return;
    }
    if (!content?.id) {
      toast.error('Unable to add — content data missing');
      return;
    }
    if (watchlistLoading) return;
    setWatchlistLoading(true);
    const itemTitle = details?.title || details?.name || content.title || content.name || 'Untitled';
    try {
      if (isInWatchlist) {
        await axios.delete(`${API_URL}/api/watchlist/${user.id}/${content.id}`);
        setIsInWatchlist(false);
        toast.success(`Removed from Watchlist`, { description: itemTitle });
      } else {
        await axios.post(`${API_URL}/api/watchlist`, null, {
          params: {
            user_id: user.id,
            tmdb_id: content.id,
            media_type: mediaType,
            title: itemTitle,
            poster_path: details?.poster_path || content.poster_path || '',
            overview: (details?.overview || content.overview || '').substring(0, 200),
            vote_average: details?.vote_average || content.vote_average || 0,
          }
        });
        setIsInWatchlist(true);
        toast.success(`Added to Watchlist`, { description: itemTitle });
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setIsInWatchlist(true);
        toast.info('Already in your Watchlist');
      } else {
        console.error('Watchlist toggle error:', err.response?.status, err.response?.data || err.message);
        toast.error('Something went wrong. Please try again.');
      }
    }
    setWatchlistLoading(false);
  };

  const loadDetails = async () => {
    setLoading(true);
    setShowTrailer(false);
    
    const data = mediaType === 'movie'
      ? await fetchMovieDetails(content.id)
      : await fetchTVDetails(content.id);
    
    setDetails(data);
    setLoading(false);
    
    // Load aggregate rating
    loadAggregateRating();
  };

  const loadUserRatings = async () => {
    if (!user || !content) return;
    
    try {
      if (mediaType === 'tv') {
        // Load all TV ratings
        const response = await axios.get(
          `${API_URL}/api/ratings/tv/${content.id}/all?user_id=${user.id}`
        );
        setUserRating(response.data.overall || 0);
        setSeasonRatings(response.data.seasons || {});
        setEpisodeRatings(response.data.episodes || {});
        setAggregateRating(response.data.aggregate?.overall || { average: 0, count: 0 });
      } else {
        // Load movie rating
        const response = await axios.get(
          `${API_URL}/api/ratings/content/${content.id}?media_type=movie&user_id=${user.id}`
        );
        setUserRating(response.data.user_rating?.rating || 0);
        setAggregateRating({
          average: response.data.average_rating,
          count: response.data.total_ratings
        });
      }
    } catch (error) {
      console.error('Failed to load ratings:', error);
    }
  };

  const loadAggregateRating = async () => {
    if (!content) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/ratings/content/${content.id}?media_type=${mediaType}`
      );
      setAggregateRating({
        average: response.data.average_rating,
        count: response.data.total_ratings
      });
    } catch (error) {
      console.error('Failed to load aggregate rating:', error);
    }
  };

  const handleRateContent = async (rating) => {
    if (!user) {
      alert('Please log in to rate content');
      return;
    }
    
    setRatingLoading(true);
    try {
      if (rating === 0) {
        // Delete rating
        await axios.delete(
          `${API_URL}/api/ratings?user_id=${user.id}&tmdb_id=${content.id}&media_type=${mediaType}`
        );
      } else {
        // Create/update rating
        await axios.post(`${API_URL}/api/ratings`, null, {
          params: {
            user_id: user.id,
            tmdb_id: content.id,
            media_type: mediaType,
            title: details?.title || details?.name || content.title || content.name,
            poster_path: details?.poster_path || content.poster_path || '',
            rating: rating
          }
        });
      }
      setUserRating(rating);
      loadAggregateRating();
    } catch (error) {
      console.error('Failed to rate content:', error);
    }
    setRatingLoading(false);
  };

  const handleRateSeason = async (seasonNumber, rating) => {
    if (!user) return;
    
    try {
      if (rating === 0) {
        await axios.delete(
          `${API_URL}/api/ratings?user_id=${user.id}&tmdb_id=${content.id}&media_type=season&season_number=${seasonNumber}`
        );
      } else {
        await axios.post(`${API_URL}/api/ratings`, null, {
          params: {
            user_id: user.id,
            tmdb_id: content.id,
            media_type: 'season',
            title: `${details?.name || content.name} - Season ${seasonNumber}`,
            poster_path: details?.poster_path || '',
            rating: rating,
            season_number: seasonNumber,
            parent_tmdb_id: content.id
          }
        });
      }
      setSeasonRatings(prev => ({ ...prev, [seasonNumber]: rating }));
    } catch (error) {
      console.error('Failed to rate season:', error);
    }
  };

  const handleRateEpisode = async (seasonNumber, episodeNumber, rating) => {
    if (!user) return;
    
    try {
      const key = `${seasonNumber}_${episodeNumber}`;
      if (rating === 0) {
        await axios.delete(
          `${API_URL}/api/ratings?user_id=${user.id}&tmdb_id=${content.id}&media_type=episode&season_number=${seasonNumber}&episode_number=${episodeNumber}`
        );
      } else {
        await axios.post(`${API_URL}/api/ratings`, null, {
          params: {
            user_id: user.id,
            tmdb_id: content.id,
            media_type: 'episode',
            title: `${details?.name || content.name} S${seasonNumber}E${episodeNumber}`,
            poster_path: '',
            rating: rating,
            season_number: seasonNumber,
            episode_number: episodeNumber,
            parent_tmdb_id: content.id
          }
        });
      }
      setEpisodeRatings(prev => ({ ...prev, [key]: rating }));
    } catch (error) {
      console.error('Failed to rate episode:', error);
    }
  };

  if (!content) return null;

  const title = details?.title || details?.name || content.title || content.name;
  const overview = details?.overview || content.overview;
  const backdropUrl = getImageUrl(
    details?.backdrop_path || content.backdrop_path,
    imageSize.backdrop
  );
  const releaseDate = details?.release_date || details?.first_air_date;
  const rating = details?.vote_average || content.vote_average;
  const trailerKey = details?.videos ? getTrailerKey(details.videos) : null;

  const genres = details?.genres?.map(g => g.name).join(', ') || '';
  const runtime = details?.runtime
    ? `${Math.floor(details.runtime / 60)}h ${details.runtime % 60}m`
    : details?.number_of_seasons
    ? `${details.number_of_seasons} Season${details.number_of_seasons > 1 ? 's' : ''}`
    : '';

  // Get seasons for TV show rating
  const seasons = details?.seasons?.filter(s => s.season_number > 0).map(s => ({
    season_number: s.season_number,
    name: s.name,
    episode_count: s.episode_count,
    episodes: Array.from({ length: s.episode_count }, (_, i) => ({
      episode_number: i + 1,
      name: `Episode ${i + 1}`
    }))
  })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 bg-zinc-900 text-white border-none overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/80 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Hero Section with Backdrop */}
          <div className="relative h-[400px] overflow-hidden">
            {showTrailer && trailerKey ? (
              <div className="absolute inset-0 bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&mute=${muted ? 1 : 0}&controls=0&showinfo=0&rel=0`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title="Trailer"
                />
                <button
                  onClick={() => setMuted(!muted)}
                  className="absolute bottom-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/80 transition-colors"
                >
                  {muted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
                </button>
              </div>
            ) : (
              <>
                <img
                  src={backdropUrl}
                  alt={title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />
              </>
            )}

            {/* Content Info Overlay */}
            {!loading && (
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h1 className="text-4xl font-bold mb-4">{title}</h1>
                
                <div className="flex items-center gap-4 flex-wrap">
                  {trailerKey ? (
                    <Button
                      onClick={() => setShowTrailer(true)}
                      size="lg"
                      className="bg-white text-black hover:bg-gray-200 font-semibold px-6 transition-all duration-200"
                    >
                      <Play className="mr-2 h-5 w-5 fill-current" />
                      Play Trailer
                    </Button>
                  ) : (
                    <Button
                      disabled
                      size="lg"
                      className="bg-gray-500 text-white font-semibold px-6 cursor-not-allowed"
                    >
                      <Play className="mr-2 h-5 w-5" />
                      No Trailer
                    </Button>
                  )}
                  
                  {/* Rate Button */}
                  <Button
                    onClick={() => setShowRating(!showRating)}
                    size="lg"
                    className={`font-semibold px-6 transition-all duration-200 ${
                      userRating > 0 
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                        : 'bg-zinc-700 hover:bg-zinc-600 text-white'
                    }`}
                  >
                    <Trophy className={`mr-2 h-5 w-5 ${userRating > 0 ? 'fill-current' : ''}`} />
                    {userRating > 0 ? `Rated ${userRating}` : 'Rate'}
                  </Button>
                  
                  <button 
                    onClick={handleToggleWatchlist}
                    disabled={watchlistLoading}
                    className={`p-3 rounded-full border-2 transition-all duration-200 ${
                      isInWatchlist 
                        ? 'border-white bg-white text-black' 
                        : 'border-gray-400 hover:border-white'
                    } ${watchlistLoading ? 'opacity-60' : ''}`}
                    title={isInWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist'}
                    data-testid="watchlist-toggle-btn"
                  >
                    {isInWatchlist ? <Check className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Next Episode Countdown Banner */}
          {mediaType === 'tv' && !nextEpLoading && nextEpisode && (
            <div data-testid="next-episode-banner" className="px-4 sm:px-8 py-4 border-b border-zinc-700">
              {nextEpisode.found ? (
                <div className="bg-gradient-to-r from-zinc-800 to-zinc-800/60 rounded-xl p-4 border border-zinc-700/50">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${countdown === 'LIVE_NOW' ? 'bg-red-600/20' : 'bg-amber-900/20'}`}>
                      {countdown === 'LIVE_NOW' ? (
                        <Radio className="h-5 w-5 text-red-500 animate-pulse" />
                      ) : (
                        <Clock className="h-5 w-5 text-amber-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-0.5">Next Episode</p>
                      {(nextEpisode.season_number > 0 || nextEpisode.episode_number > 0) && (
                        <p className="text-white text-sm font-semibold">
                          S{String(nextEpisode.season_number).padStart(2, '0')} E{String(nextEpisode.episode_number).padStart(2, '0')}
                          {nextEpisode.episode_title ? ` – ${nextEpisode.episode_title}` : ''}
                        </p>
                      )}
                      {nextEpisode.air_datetime && (
                        <p className="text-gray-400 text-xs mt-0.5" data-testid="next-episode-airdate">
                          Airs: {new Date(nextEpisode.air_datetime).toLocaleDateString('en-US', {
                            weekday: 'long', month: 'long', day: 'numeric',
                          })} at {new Date(nextEpisode.air_datetime).toLocaleTimeString('en-US', {
                            hour: 'numeric', minute: '2-digit', hour12: true,
                          })}
                        </p>
                      )}
                      {/* Countdown display */}
                      {countdown && countdown !== 'AIRED' && (
                        <div className="mt-2" data-testid="next-episode-countdown">
                          {countdown === 'LIVE_NOW' ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600/20 border border-red-500/40 text-red-400 text-xs font-bold uppercase">
                              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                              Live Now
                            </span>
                          ) : (
                            <p className="text-amber-400 font-mono text-sm sm:text-base font-bold tracking-wide">
                              {countdown}
                            </p>
                          )}
                        </div>
                      )}
                      {countdown === 'AIRED' && (
                        <p className="text-gray-500 text-xs mt-1">This episode has aired</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/30">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-zinc-500 flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">Next Episode</p>
                      <p className="text-gray-500 text-sm mt-0.5" data-testid="next-episode-none">
                        {nextEpisode.message || 'No upcoming episode currently scheduled'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rating Panel (Expandable) */}
          {showRating && (
            <div className="px-8 py-6 bg-zinc-800/50 border-y border-zinc-700">
              <div className="flex items-start justify-between gap-8">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    Rate this {mediaType === 'movie' ? 'Movie' : 'Series'}
                  </h3>
                  
                  {user ? (
                    <ContentRating
                      contentType={mediaType}
                      value={userRating}
                      seasonRatings={seasonRatings}
                      episodeRatings={episodeRatings}
                      onRateContent={handleRateContent}
                      onRateSeason={handleRateSeason}
                      onRateEpisode={handleRateEpisode}
                      seasons={seasons}
                    />
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-400 mb-2">Log in to rate this content</p>
                      <Button
                        onClick={() => window.location.href = '/login'}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Sign In
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Aggregate Score */}
                {aggregateRating.count > 0 && (
                  <div className="text-center">
                    <p className="text-gray-400 text-sm mb-2">Community Rating</p>
                    <AggregateRating 
                      averageRating={aggregateRating.average} 
                      totalRatings={aggregateRating.count}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Details Section */}
          <div className="p-8 space-y-4">
            <div className="flex items-center gap-4 text-sm">
              {rating && (
                <span className="text-green-400 font-semibold">
                  {(rating * 10).toFixed(0)}% Match
                </span>
              )}
              {releaseDate && (
                <span className="text-gray-400">
                  {new Date(releaseDate).getFullYear()}
                </span>
              )}
              {runtime && <span className="text-gray-400">{runtime}</span>}
              
              {/* Show user's rating inline */}
              {userRating > 0 && (
                <span className="flex items-center gap-1 text-yellow-500">
                  <Trophy className="h-4 w-4" fill="currentColor" />
                  Your rating: {userRating}
                </span>
              )}
            </div>

            <p className="text-gray-300 text-base leading-relaxed">
              {overview}
            </p>

            {genres && (
              <div className="pt-4 border-t border-gray-700">
                <span className="text-gray-400">Genres: </span>
                <span className="text-white">{genres}</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContentModal;
